import React, { useState, useRef, useEffect, useCallback } from "react";
import Draggable from "react-draggable";

/**
 * A non-blocking, draggable, and resizable modal component.
 *
 * Features:
 * - Draggable by the header/title bar
 * - Resizable from edges and corners
 * - Non-blocking overlay (allows interaction with page behind)
 * - Starts centered on screen
 */
const DraggableResizableModal = ({
  isOpen,
  onClose,
  title = "Modal",
  children,
  defaultSize = { width: 600, height: 400 },
  minSize = { width: 300, height: 200 },
  defaultPosition = null,
  zIndex = 10000,
  headerStyle = {},
  contentStyle = {},
  showCloseButton = true,
  resizable = true,
}) => {
  const nodeRef = useRef(null);
  const [size, setSize] = useState(defaultSize);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef({
    direction: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    startPosX: 0,
    startPosY: 0,
  });

  // Calculate center position when modal opens
  useEffect(() => {
    if (isOpen && !isInitialized) {
      const width = defaultSize.width;
      const height = defaultSize.height;
      const centerX = Math.max(20, (window.innerWidth - width) / 2);
      const centerY = Math.max(20, (window.innerHeight - height) / 2);

      if (defaultPosition) {
        setPosition(defaultPosition);
      } else {
        setPosition({ x: centerX, y: centerY });
      }
      setSize(defaultSize);
      setIsInitialized(true);
    }
  }, [isOpen, isInitialized, defaultSize, defaultPosition]);

  // Reset initialization when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen]);

  // Handle resize
  const handleResizeStart = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();

    resizeRef.current = {
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
      startPosX: position.x,
      startPosY: position.y,
    };

    setIsResizing(true);
  }, [size, position]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const { direction, startX, startY, startWidth, startHeight, startPosX, startPosY } = resizeRef.current;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newPosX = startPosX;
      let newPosY = startPosY;

      // Handle horizontal resizing
      if (direction.includes("e")) {
        newWidth = Math.max(minSize.width, startWidth + deltaX);
      }
      if (direction.includes("w")) {
        const widthChange = Math.min(deltaX, startWidth - minSize.width);
        newWidth = startWidth - widthChange;
        newPosX = startPosX + widthChange;
      }

      // Handle vertical resizing
      if (direction.includes("s")) {
        newHeight = Math.max(minSize.height, startHeight + deltaY);
      }
      if (direction.includes("n")) {
        const heightChange = Math.min(deltaY, startHeight - minSize.height);
        newHeight = startHeight - heightChange;
        newPosY = startPosY + heightChange;
      }

      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newPosX, y: newPosY });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, minSize]);

  const handleDrag = useCallback((e, data) => {
    setPosition({ x: data.x, y: data.y });
  }, []);

  if (!isOpen || !isInitialized) return null;

  const resizeHandleStyle = {
    position: "absolute",
    zIndex: 10,
  };

  const cornerHandleSize = 14;
  const edgeHandleThickness = 8;

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".modal-drag-handle"
      cancel=".modal-close-btn"
      position={position}
      onDrag={handleDrag}
      disabled={isResizing}
    >
      <div
        ref={nodeRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: zIndex,
          width: size.width,
          height: size.height,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Header / Drag Handle */}
        <div
          className="modal-drag-handle"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            backgroundColor: "#f7fafc",
            borderBottom: "1px solid #e2e8f0",
            cursor: "move",
            userSelect: "none",
            flexShrink: 0,
            ...headerStyle,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "600",
              color: "#2d3748",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {title}
          </h2>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="modal-close-btn"
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#718096",
                padding: "4px 8px",
                borderRadius: "4px",
                lineHeight: 1,
                marginLeft: "8px",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#edf2f7")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              ×
            </button>
          )}
        </div>

        {/* Content */}
        <div
          onWheelCapture={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight > el.clientHeight) {
              el.scrollTop += e.deltaY;
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            padding: "16px",
            minHeight: 0,
            ...contentStyle,
          }}
        >
          {children}
        </div>

        {/* Resize Handles */}
        {resizable && (
          <>
            {/* Right edge */}
            <div
              style={{
                ...resizeHandleStyle,
                right: 0,
                top: cornerHandleSize,
                bottom: cornerHandleSize,
                width: edgeHandleThickness,
                cursor: "ew-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "e")}
            />
            {/* Bottom edge */}
            <div
              style={{
                ...resizeHandleStyle,
                bottom: 0,
                left: cornerHandleSize,
                right: cornerHandleSize,
                height: edgeHandleThickness,
                cursor: "ns-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "s")}
            />
            {/* Left edge */}
            <div
              style={{
                ...resizeHandleStyle,
                left: 0,
                top: cornerHandleSize,
                bottom: cornerHandleSize,
                width: edgeHandleThickness,
                cursor: "ew-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "w")}
            />
            {/* Top edge */}
            <div
              style={{
                ...resizeHandleStyle,
                top: 0,
                left: cornerHandleSize,
                right: cornerHandleSize,
                height: edgeHandleThickness,
                cursor: "ns-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "n")}
            />
            {/* Bottom-right corner */}
            <div
              style={{
                ...resizeHandleStyle,
                right: 0,
                bottom: 0,
                width: cornerHandleSize,
                height: cornerHandleSize,
                cursor: "nwse-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "se")}
            />
            {/* Bottom-left corner */}
            <div
              style={{
                ...resizeHandleStyle,
                left: 0,
                bottom: 0,
                width: cornerHandleSize,
                height: cornerHandleSize,
                cursor: "nesw-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "sw")}
            />
            {/* Top-right corner */}
            <div
              style={{
                ...resizeHandleStyle,
                right: 0,
                top: 0,
                width: cornerHandleSize,
                height: cornerHandleSize,
                cursor: "nesw-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "ne")}
            />
            {/* Top-left corner */}
            <div
              style={{
                ...resizeHandleStyle,
                left: 0,
                top: 0,
                width: cornerHandleSize,
                height: cornerHandleSize,
                cursor: "nwse-resize",
              }}
              onMouseDown={(e) => handleResizeStart(e, "nw")}
            />
          </>
        )}
      </div>
    </Draggable>
  );
};

export default DraggableResizableModal;
